import { useCallback, useEffect, useRef, useState } from "react";

import {
  createSupportSession,
  endSupportSession,
  getSupportSession,
  isOpen,
  listSupportSessions,
  type SupportSession,
  type SupportSessionStatus,
} from "../api/support";

type Props = {
  agentId: string;
  /** Hide the request UI when the host is unreachable — sending help requests
   * the agent can't pick up just pile up in 'requested' state. */
  disabled?: boolean;
};

// Poll every 5 s while a session is in flight so the customer sees
// REQUESTED → AWAITING_CONSENT → ACTIVE/DENIED transitions without
// having to refresh. Backend transitions happen on agent heartbeat
// (~60 s max) + task completion; 5 s is responsive enough without
// hammering the API.
const POLL_MS = 5_000;

/** Request remote help from KraetdTech for the parent agent. Shows the
 * current open session (if any) so the customer doesn't accidentally
 * fire off duplicates. Polls live sessions until they reach a terminal
 * state so the customer sees what's happening in near-real-time.
 */
export default function GetHelpSection({ agentId, disabled = false }: Props) {
  const [session, setSession] = useState<SupportSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Initial load: find any in-flight session for this agent.
  useEffect(() => {
    let cancelled = false;
    listSupportSessions()
      .then((all) => {
        if (cancelled || !mounted.current) return;
        const open = all.find((s) => s.target_agent === agentId && isOpen(s.status));
        // Fall back to the most recent terminal session for this agent
        // so the user sees "Your last request was denied" rather than a
        // blank slate after the agent reports back.
        if (open) {
          setSession(open);
        } else {
          const recent = all
            .filter((s) => s.target_agent === agentId)
            .sort((a, b) => b.requested_at.localeCompare(a.requested_at))[0];
          if (recent && wasRecent(recent)) setSession(recent);
        }
      })
      .catch(() => {
        // Soft-fail — the section will just show the request button.
      })
      .finally(() => {
        if (!cancelled && mounted.current) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  // Live polling: while we have a session and it's still in flight,
  // re-fetch it every POLL_MS so the customer sees state transitions
  // (REQUESTED → AWAITING_CONSENT → ACTIVE/DENIED).
  useEffect(() => {
    if (!session || !isOpen(session.status)) return;
    const sid = session.id;
    const interval = setInterval(async () => {
      try {
        const fresh = await getSupportSession(sid);
        if (!mounted.current) return;
        setSession(fresh);
      } catch {
        // Soft-fail — stick with the cached session; next tick may succeed.
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [session]);

  async function onCreate() {
    setSubmitting(true);
    setError(null);
    try {
      const created = await createSupportSession(agentId, reason.trim());
      if (mounted.current) {
        setSession(created);
        setShowForm(false);
        setReason("");
      }
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (mounted.current) {
        setError(detail ?? "Couldn't send the help request. Please try again.");
      }
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }

  async function onCancel() {
    if (!session) return;
    if (!window.confirm("Cancel this help request?")) return;
    setSubmitting(true);
    setError(null);
    try {
      const ended = await endSupportSession(session.id);
      if (mounted.current) setSession(ended);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (mounted.current) {
        setError(detail ?? "Couldn't cancel the request. Please try again.");
      }
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  }

  const dismissTerminal = useCallback(() => {
    setSession(null);
    setError(null);
  }, []);

  if (loading) return null;

  // In-flight session → polling status panel + cancel button.
  if (session && isOpen(session.status)) {
    return (
      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
          Remote help
        </h2>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">
            Help request {statusCopy(session.status)}.
          </p>
          {session.reason ? (
            <p className="mt-1 text-xs text-amber-900/80">"{session.reason}"</p>
          ) : null}
          <p className="mt-2 text-xs text-amber-900/80">
            KraetdTech will be in touch. You'll see a prompt on this computer asking
            for permission before anyone connects.
          </p>
          {error ? (
            <p role="alert" className="mt-2 text-xs text-red-700">
              {error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="no-tap-highlight mt-3 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50"
          >
            {submitting ? "Cancelling…" : "Cancel request"}
          </button>
        </div>
      </section>
    );
  }

  // Just-terminated session — show outcome briefly so the customer
  // knows what happened, with a "request again" path. Only shown if
  // the session is recent (last ~10 min) so revisiting the page hours
  // later doesn't surface a stale "denied" panel.
  if (session && !isOpen(session.status) && wasRecent(session)) {
    return (
      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
          Remote help
        </h2>
        <TerminalPanel session={session} onDismiss={dismissTerminal} />
      </section>
    );
  }

  return (
    <section>
      <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
        Remote help
      </h2>
      {showForm ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700">
              What's going on with this computer?
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              rows={3}
              placeholder="e.g. Printer keeps spitting out blank pages"
              className="mt-1 block w-full rounded-lg border border-slate-300 p-3 text-sm focus:border-slate-500 focus:outline-none"
            />
            <span className="mt-1 block text-xs text-slate-500">
              Optional — helps support get to the issue faster.
            </span>
          </label>
          {error ? (
            <p role="alert" className="mt-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {error}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onCreate}
              disabled={submitting}
              className="no-tap-highlight rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send request"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setReason("");
                setError(null);
              }}
              className="no-tap-highlight rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center">
          <p className="text-sm text-slate-500">
            Stuck on this computer? KraetdTech can take a look remotely.
          </p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            disabled={disabled}
            className="no-tap-highlight mt-3 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            title={disabled ? "Wait for this computer to come back online" : undefined}
          >
            Get help
          </button>
        </div>
      )}
    </section>
  );
}

function TerminalPanel({
  session,
  onDismiss,
}: {
  session: SupportSession;
  onDismiss: () => void;
}) {
  const { tone, headline, body } = terminalCopy(session);
  return (
    <div
      className={`rounded-2xl border p-4 ${
        tone === "good"
          ? "border-green-200 bg-green-50"
          : "border-slate-200 bg-slate-50"
      }`}
    >
      <p
        className={`text-sm font-medium ${
          tone === "good" ? "text-green-900" : "text-slate-900"
        }`}
      >
        {headline}
      </p>
      <p className={`mt-1 text-xs ${tone === "good" ? "text-green-900/80" : "text-slate-600"}`}>
        {body}
      </p>
      <button
        type="button"
        onClick={onDismiss}
        className="no-tap-highlight mt-3 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        Dismiss
      </button>
    </div>
  );
}

function statusCopy(s: SupportSessionStatus): string {
  switch (s) {
    case "requested":
      return "sent";
    case "awaiting_consent":
      return "waiting for permission on this PC";
    case "active":
      return "in progress";
    default:
      return s;
  }
}

function terminalCopy(s: SupportSession): {
  tone: "good" | "neutral";
  headline: string;
  body: string;
} {
  switch (s.status) {
    case "ended":
      return {
        tone: "good",
        headline: "Help session ended.",
        body:
          s.end_reason === "support_ended"
            ? "KraetdTech support closed the session. Hope they got you sorted."
            : "Session closed. Open a new request if you need more help.",
      };
    case "denied":
      return {
        tone: "neutral",
        headline: "Help request was denied.",
        body:
          "Either no one accepted on this PC, or the consent prompt timed out. " +
          "If you weren't at the PC when it happened, just request help again.",
      };
    case "expired":
      return {
        tone: "neutral",
        headline: "Help request expired.",
        body:
          "Nobody clicked Allow on the consent prompt within 15 minutes. " +
          "Request again when someone's at the PC.",
      };
    default:
      return {
        tone: "neutral",
        headline: "Help request closed.",
        body: "Open a new request if you need help.",
      };
  }
}

// Treat a terminal session as "recent" for ~10 min so we surface the
// outcome on the next page load, but don't make a customer see a
// stale "denied" panel for hours.
function wasRecent(s: SupportSession): boolean {
  const ref = s.ended_at ?? s.requested_at;
  const ageMs = Date.now() - new Date(ref).getTime();
  return ageMs < 10 * 60 * 1000;
}
